import { useCallback } from 'react'

import { useNavigate } from 'react-router'
import { useSearchParams } from 'react-router-dom'

import { ClientError } from '@tribeplatform/gql-client/lib'
import {
  useArchiveChat,
  useBlockMember,
  useChat,
  useLeaveGroupChat,
  useUnarchiveChat,
  useUnblockMember,
} from '@tribeplatform/react-sdk/hooks'
import { confirm } from '@tribeplatform/react-ui-kit/Dialog'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { useI18n } from '../../i18n/index.js'

interface Props {
  chatId: string
}

export const useChatActions = ({ chatId }: Props) => {
  const { data: chat } = useChat({ variables: { id: chatId } })
  const { mutateAsync: archiveChatMutation } = useArchiveChat()
  const { mutateAsync: unarchiveChatMutation } = useUnarchiveChat()
  const { mutateAsync: blockMemberMutation, isLoading: isBlocking } =
    useBlockMember()
  const { mutateAsync: unblockMemberMutation, isLoading: isUnblocking } =
    useUnblockMember()
  const navigate = useNavigate()
  const { $t } = useI18n()
  const { mutateAsync: leaveGroupChatMutation } = useLeaveGroupChat()
  const [searchParams] = useSearchParams()

  const archiveChat = useCallback(async () => {
    const confirmed = await confirm({
      title: $t(
        {
          defaultMessage: 'Archive this chat?',
          id: 'Messaging.ArchiveChat.Confirmation.Title',
        },
        {
          chat_name: chat?.name,
        },
      ),
      description: $t(
        {
          defaultMessage: 'You can always unarchive it later.',
          id: 'Messaging.ArchiveChat.Confirmation.Description',
        },
        {
          member_name: chat?.name,
        },
      ),
      proceedLabel: $t({
        defaultMessage: 'Archive',
        id: 'Generics.Archive',
      }),
      danger: true,
    })

    if (!confirmed) {
      return
    }

    try {
      await archiveChatMutation({
        chatId,
      })
      toast({
        title: $t({
          id: 'Generics.Success',
          defaultMessage: 'Success',
        }),
        description: $t({
          id: 'Messaging.ArchiveChat.Success',
          defaultMessage: 'The chat has been archived',
        }),
        status: 'success',
      })
      navigate(`/messaging?${searchParams.toString()}`)
    } catch (error) {
      toast({
        title: $t({
          id: 'Generics.Error',
          defaultMessage: 'Error',
        }),
        description: $t({
          id: 'Messaging.ArchiveChat.Error',
          defaultMessage: 'Failed to archive the chat',
        }),
        status: 'error',
      })
    }
  }, [$t, archiveChatMutation, chat?.name, chatId, navigate, searchParams])

  const unarchiveChat = useCallback(async () => {
    try {
      await unarchiveChatMutation({
        chatId,
      })
      toast({
        title: $t({
          id: 'Generics.Success',
          defaultMessage: 'Success',
        }),
        description: $t({
          id: 'Messaging.UnarchiveChat.Success',
          defaultMessage: 'The chat has been unarchived',
        }),
        status: 'success',
      })
      navigate(`/messaging?${searchParams.toString()}`)
    } catch (error) {
      toast({
        title: $t({
          id: 'Generics.Error',
          defaultMessage: 'Error',
        }),
        description: $t({
          id: 'Messaging.UnarchiveChat.Error',
          defaultMessage: 'Failed to unarchive the chat',
        }),
        status: 'error',
      })
    }
  }, [unarchiveChatMutation, chatId, $t, navigate, searchParams])

  const blockMember = useCallback(
    async (memberId: string) => {
      const confirmed = await confirm({
        title: $t(
          {
            defaultMessage: 'Are you sure you want to block?',
            id: 'Messaging.Block.Confirmation',
          },
          {
            member_name: memberId,
          },
        ),
        description: $t(
          {
            defaultMessage:
              'When you block a {MEMBER}, they won’t be able to message you or vice versa. They won’t know that you’ve blocked them.',
            id: 'Messaging.Block.Confirmation.Description',
          },
          {
            member_name: memberId,
          },
        ),
        proceedLabel: $t({
          defaultMessage: 'Block',
          id: 'Messaging.Block',
        }),
        danger: true,
      })

      if (!confirmed) {
        return
      }

      try {
        await blockMemberMutation({ memberId })
        toast({
          title: $t({
            id: 'Generics.Success',
            defaultMessage: 'Success',
          }),
          description: $t({
            id: 'Messaging.Block.Success',
            defaultMessage: 'The member has been blocked',
          }),
          status: 'success',
        })
      } catch (error) {
        toast({
          title: $t({
            id: 'Generics.Error',
            defaultMessage: 'Error',
          }),
          description: $t({
            id: 'Messaging.Block.Error',
            defaultMessage: 'Failed to block the member',
          }),
          status: 'error',
        })
      }
    },
    [$t, blockMemberMutation],
  )

  const unblockMember = useCallback(
    async (memberId: string) => {
      try {
        await unblockMemberMutation({ memberId })
        toast({
          title: $t({
            id: 'Generics.Success',
            defaultMessage: 'Success',
          }),
          description: $t({
            id: 'Messaging.Unblock.Success',
            defaultMessage: 'The member has been unblocked',
          }),
          status: 'success',
        })
      } catch (error) {
        toast({
          title: $t({
            id: 'Generics.Error',
            defaultMessage: 'Error',
          }),
          description: $t({
            id: 'Messaging.Unblock.Error',
            defaultMessage: 'Failed to unblock the member',
          }),
          status: 'error',
        })
      }
    },
    [$t, unblockMemberMutation],
  )

  const leaveGroupChat = useCallback(async () => {
    const confirmed = await confirm({
      title: $t(
        {
          defaultMessage: 'Leave this group chat?',
          id: 'Messaging.LeaveGroupChat.Confirmation.Title',
        },
        {
          chat_name: chat?.name,
        },
      ),
      description: $t(
        {
          defaultMessage: 'You won’t be able to see messages from this group.',
          id: 'Messaging.LeaveGroupChat.Confirmation.Description',
        },
        {
          member_name: chat?.name,
        },
      ),
      proceedLabel: $t({
        defaultMessage: 'Leave',
        id: 'Generics.Leave',
      }),
      danger: true,
    })

    if (!confirmed) {
      return
    }

    try {
      await leaveGroupChatMutation({
        chatId,
      })
      toast({
        title: $t({
          id: 'Generics.Success',
          defaultMessage: 'Success',
        }),
        description: $t({
          id: 'Messaging.LeaveGroupChat.Success',
          defaultMessage: 'You have left the group chat',
        }),
        status: 'success',
      })
    } catch (error) {
      const message = (error as ClientError)?.response?.errors[0].message
      toast({
        title: $t({
          id: 'Generics.Error',
          defaultMessage: 'Error',
        }),
        description:
          message ??
          $t({
            id: 'Messaging.LeaveGroupChat.Error',
            defaultMessage: 'Failed to leave the group chat',
          }),
        status: 'error',
      })
    }
  }, [$t, leaveGroupChatMutation, chat?.name, chatId])

  return {
    archiveChat,
    unarchiveChat,
    blockMember,
    leaveGroupChat,
    unblockMember,
    isBlocking,
    isUnblocking,
  }
}
